import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '@/settings/settings';
import { useSoundCues } from './useSoundCues';

// Minimal local types for the Web Speech API (not in all TS DOM lib versions).
interface SpeechRecognitionAlternative { readonly transcript: string }
interface SpeechRecognitionResult { readonly length: number; [i: number]: SpeechRecognitionAlternative }
interface SpeechRecognitionResultList { readonly length: number; [i: number]: SpeechRecognitionResult }
interface SpeechRecognitionEvent { readonly results: SpeechRecognitionResultList }
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((evt: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

export interface VoiceHook {
  /** True when both the browser and the user's settings support voice. */
  voiceAvailable: boolean;
  recognitionSupported: boolean;
  synthesisSupported: boolean;
  /** Currently recording via the microphone. */
  listening: boolean;
  /** Currently speaking via synthesis. */
  speaking: boolean;
  /** Start microphone capture; calls onResult with the final transcript. */
  startListening: (onResult: (text: string) => void) => void;
  stopListening: () => void;
  /** Speak text via synthesis. No-op when voice/synthesis unavailable. */
  speak: (text: string) => void;
  cancelSpeech: () => void;
}

export function useVoice(): VoiceHook {
  const { settings } = useSettings();
  const { listenStart, listenStop } = useSoundCues();

  const recognitionSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const synthesisSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  const voiceAvailable = settings.voice && (recognitionSupported || synthesisSupported);

  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const recoRef = useRef<SpeechRecognitionInstance | null>(null);
  const onResultRef = useRef<((text: string) => void) | null>(null);

  const stopListening = useCallback(() => {
    if (recoRef.current) {
      try {
        recoRef.current.stop();
      } catch {
        /* ignore if already stopped */
      }
      recoRef.current = null;
    }
    listenStop();
    setListening(false);
  }, [listenStop]);

  const startListening = useCallback(
    (onResult: (text: string) => void) => {
      if (!recognitionSupported || !settings.voice) return;
      stopListening();
      onResultRef.current = onResult;

      const w = window as unknown as Record<string, unknown>;
      const SR = (w['SpeechRecognition'] ?? w['webkitSpeechRecognition']) as
        | SpeechRecognitionCtor
        | undefined;
      if (!SR) return;

      const reco = new SR();
      reco.continuous = false;
      reco.interimResults = false;
      reco.lang = 'en-AU';

      reco.onresult = (evt: SpeechRecognitionEvent) => {
        const parts: string[] = [];
        for (let i = 0; i < evt.results.length; i++) {
          const alt = evt.results[i]?.[0];
          if (alt) parts.push(alt.transcript);
        }
        const transcript = parts.join(' ').trim();
        if (transcript && onResultRef.current) {
          onResultRef.current(transcript);
        }
      };

      reco.onend = () => {
        listenStop();
        setListening(false);
        recoRef.current = null;
      };

      reco.onerror = () => {
        listenStop();
        setListening(false);
        recoRef.current = null;
      };

      recoRef.current = reco;
      reco.start();
      listenStart();
      setListening(true);
    },
    [recognitionSupported, settings.voice, stopListening, listenStart, listenStop],
  );

  const cancelSpeech = useCallback(() => {
    if (synthesisSupported) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, [synthesisSupported]);

  const speak = useCallback(
    (text: string) => {
      if (!synthesisSupported || !settings.voice || !text.trim()) return;
      cancelSpeech();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-AU';
      utterance.rate = 0.95;
      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
    },
    [synthesisSupported, settings.voice, cancelSpeech],
  );

  // Stop everything when voice is turned off mid-session.
  useEffect(() => {
    if (!settings.voice) {
      stopListening();
      cancelSpeech();
    }
  }, [settings.voice, stopListening, cancelSpeech]);

  return {
    voiceAvailable,
    recognitionSupported,
    synthesisSupported,
    listening,
    speaking,
    startListening,
    stopListening,
    speak,
    cancelSpeech,
  };
}
